/**
 * Per-surface privacy audit.
 *
 * Drives the "Privacy Verification Checklist" section on the /privacy page.
 * Edit here when a new surface is added or new local-storage keys are
 * introduced - never duplicate this list inline in components.
 *
 * Rule for every entry: `network` MUST stay "none". If a surface ever needs
 * to send data to a server, that's a privacy-policy change, not a checklist
 * tweak.
 */

export type PrivacySurface = {
  /** Display label for the surface. */
  name: string;
  /** Where it lives in the app (route or area). */
  area: string;
  /** Local-storage keys (or key prefixes) it reads/writes. Empty = none. */
  storageKeys: string[];
  /** Outbound network calls carrying user input. Always "none". */
  network: "none";
  /** Short note about what is (or isn't) persisted. */
  note: string;
};

export const PRIVACY_SURFACES: PrivacySurface[] = [
  {
    name: "Play - hot-seat games",
    area: "/play/* (Element Battle, Chem Duel, etc.)",
    storageKeys: [
      "elementpad:<game>:best (solo high scores, numbers only)",
      "eb-onboarded:<game> (How-to-play dismissal flag)",
      "elementpad:sound-muted",
    ],
    network: "none",
    note: "Player nicknames live in React state for the current match only. They are never written to storage and never sent over the network.",
  },
  {
    name: "Learn - flashcards",
    area: "/learn, /learn/:deckId",
    storageKeys: [
      "elementpad:learn:<deckId> (Leitner box + due date per card)",
      "elementpad:learn:missed-from-play:cards (review deck from games)",
    ],
    network: "none",
    note: "Spaced-repetition progress and the Missed-from-Play deck are computed and stored entirely in this browser.",
  },
  {
    name: "Online Lab",
    area: "/lab",
    storageKeys: [
      "elementpad.labNotebook.v1 (saved experiments, this device only)",
      "elementpad:lab:predict-first (UI toggle)",
    ],
    network: "none",
    note: "Lab notebook entries and PDF exports are generated locally. Nothing is uploaded.",
  },
  {
    name: "Worksheet Generator & other tools",
    area: "/worksheets, /molar-mass, /balancer, /molecules, ...",
    storageKeys: [],
    network: "none",
    note: "Inputs and generated worksheets exist only in the current page session.",
  },
  {
    name: "Accessibility & theme settings",
    area: "Accessibility widget, theme toggle",
    storageKeys: [
      "a11y-settings-v1 (text size, contrast, fonts, motion)",
      "elementpad:sound-muted",
    ],
    network: "none",
    note: "Preferences only. No identifiers, no analytics tied to them.",
  },
  {
    name: "Privacy notice",
    area: "First-visit modal",
    storageKeys: ["elementpad:privacy-notice-ack (dismissal flag)"],
    network: "none",
    note: "A single boolean so the notice doesn't reappear after you dismiss it.",
  },
  {
    name: "Element of the Day",
    area: "/daily",
    storageKeys: [
      "elementpad:daily:result:<date> (per-day score, no identifiers)",
      "elementpad:daily:streak (current/best streak counters)",
      "elementpad:daily:lastPlayed (date string for streak math)",
    ],
    network: "none",
    note: "The daily element and questions are picked deterministically from the date in your browser. Results, streaks, and the share-text emoji grid are computed locally - nothing is sent to a server.",
  },
  {
    name: "Chem-doku - Puzzle of the Day",
    area: "/play/chem-doku (Daily mode)",
    storageKeys: [
      "elementpad:chemdoku-daily:progress:<date> (in-progress board, this device only)",
      "elementpad:chemdoku-daily:result:<date> (moves + seconds for the solve)",
      "elementpad:chemdoku-daily:streak (current/best streak counters)",
      "elementpad:chemdoku-daily:lastPlayed (date string for streak math)",
    ],
    network: "none",
    note: "Daily puzzle is generated deterministically from the date. Mid-puzzle progress, completion times, and streaks stay in this browser. Share text is built locally and copied to your clipboard only when you tap Share.",
  },
  {
    name: "Rubric scoring (End-of-Year Project)",
    area: "/teach/end-of-year-project",
    storageKeys: ["elementpad:rubric-scores:v1 (student name, topic, scores, notes)"],
    network: "none",
    note: "Rubric entries stay in this browser. Sharing produces a base64url-encoded URL you copy yourself; CSV/PDF exports download locally. Nothing is sent to a server.",
  },
  {
    name: "Theme, onboarding & testing-notice flags",
    area: "Global UI",
    storageKeys: [
      "theme-mode (light/dark choice)",
      "elementpad:onboarding:v1 (landing-page tour dismissal flag)",
      "testing-notice-dismissed-v1 (beta-notice dismissal flag)",
    ],
    network: "none",
    note: "Lightweight UI flags so dialogs and the theme don't reappear after you dismiss/set them. No identifiers, no analytics.",
  },
];